import * as moment from 'moment';

declare const Helpers: any;

export type ComparisonDatePicker = {
  startDate?: string | null;
  endDate?: string | null;
  trendType?: string | null;
  isCustomDateRangeSelected?: boolean | null;
};

export type ComparisonApiPayload = {
  from?: string | null;
  to?: string | null;
  is_custom_date_range_selected?: boolean | null;
  [key: string]: any;
};

/**
 * Preset range types that map to ltms-daterange-picker / calendar `trendType`.
 * Supported: week, month, quarter, year, financial_year.
 */
const PRESET_RANGE_TYPES = new Set(['week', 'month', 'quarter', 'year', 'financial_year']);

/**
 * Resolves the full preset period end for the active calendar selection.
 * Calendar UI caps endDate to today for current presets; comparison APIs need the
 * full period end (e.g. month end) so the backend resolves the correct previous period.
 */
export function getPresetPeriodEnd(startDate: moment.Moment, trendType: string): moment.Moment | null {
  switch (trendType) {
    case 'week':
      return startDate.clone().endOf('isoWeek').startOf('day');
    case 'month':
      return startDate.clone().endOf('month').startOf('day');
    case 'quarter':
      return startDate.clone().endOf('quarter').startOf('day');
    case 'year':
      return startDate.clone().endOf('year').startOf('day');
    case 'financial_year':
      return startDate.clone().add(1, 'year').quarter(1).endOf('quarter').startOf('day');
    default:
      return null;
  }
}

/**
 * Returns from/to for comparison APIs (e.g. comparison_v2, summary_by_account).
 * Extends `to` to the full preset period end only when a preset is active
 * (`isCustomDateRangeSelected === false`).
 *
 * Usage:
 * ```ts
 * import { getComparisonApiDateRange, applyComparisonApiDateRange } from 'src/app/shared/utils/comparison-api-date-range.util';
 *
 * const dates = getComparisonApiDateRange(Helpers.datePicker);
 * payload.from = dates.from;
 * payload.to = dates.to;
 *
 * // or mutate an existing payload before the API call:
 * applyComparisonApiDateRange(payload, Helpers.datePicker);
 * ```
 */
export function getComparisonApiDateRange(
  datePicker?: ComparisonDatePicker | null
): { from: string | null; to: string | null } {
  const picker = datePicker ?? Helpers?.datePicker;
  const from = picker?.startDate ?? null;
  const to = picker?.endDate ?? null;

  if (!from || !to || picker?.isCustomDateRangeSelected) {
    return { from, to };
  }

  const start = moment(from, 'YYYYMMDD');
  const displayEnd = moment(to, 'YYYYMMDD');
  if (!start.isValid() || !displayEnd.isValid()) {
    return { from, to };
  }

  const trendType = picker?.trendType ?? 'month';
  if (!PRESET_RANGE_TYPES.has(trendType)) {
    return { from, to };
  }

  const periodEnd = getPresetPeriodEnd(start, trendType);
  if (periodEnd?.isValid() && periodEnd.isAfter(displayEnd, 'day')) {
    return { from, to: periodEnd.format('YYYYMMDD') };
  }

  return { from, to };
}

/**
 * Applies preset period-end dates to an API payload. Only runs when
 * `is_custom_date_range_selected` is false. Updates payload on API call only;
 * calendar display dates are unchanged.
 */
export function applyComparisonApiDateRange(
  payload?: ComparisonApiPayload | null,
  datePicker?: ComparisonDatePicker | null
): void {
  if (!payload) {
    return;
  }

  const picker = datePicker ?? Helpers?.datePicker;
  const isCustomRange =
    payload.is_custom_date_range_selected ?? picker?.isCustomDateRangeSelected;

  if (isCustomRange) {
    return;
  }

  const { from, to } = getComparisonApiDateRange(picker);
  if (from) {
    payload.from = from;
  }
  if (to) {
    payload.to = to;
  }
}
